// 起動（2026-10-21 に app.js から分けた）
import { S } from './state.js';
import { C } from './core/model.js';
import { catLabel, txt } from './text.js';
import { anim } from './store.js';
import { $, keyOf, setStatus } from './util.js';
import { later, loadData, markInput, prefetchParts, warmParts, whenIdle } from './core/data.js';
import { buildStage } from './stage.js';
import { defaultSave, readStored } from './core/savedata.js';
import { loadSave as applySave } from './loadsave.js';
import { unzip } from './core/zlib.js';
import { buildCategories, placeIndicator, selectCategory } from './categories.js';
import { bindOpacity, bindPad, useTool } from './tools.js';
import { bindStage } from './pointer.js';
import { bindBox, scheduleBox } from './partbox.js';
import { fitPageFrame } from './frame.js';
import { resetView } from './view.js';
import { isLocked, openRandomSettings, randomize, setLock } from './random.js';
import { bindHistory, histReset, toast, undo } from './history.js';
import { bindGrip } from './sheet.js';
import { bindSide } from './side.js';
import { bindPartsNav } from './partsnav.js';
import { AUTO_KEY, flushAutosave, scheduleAutosave, takeCarried } from './storage.js';
import { openSlots } from './slots.js';
import { drawName, loadNameFont, openNameEditor } from './name.js';
import { bindFinish, finish } from './finish.js';
import { closeModal, toggleMenu } from './dialog.js';
import { agreed, appIsShown, openFirst } from './agree.js';
import { bindTour } from './tour.js';
import { on } from './core/events.js';
import type { CatStrip } from './categories.js';
import type { SaveData } from './types.js';

// ---- 起動 ----
// 読み込み中の画面（index.php の #boot）を消す。キャラとパーツの一覧を描いたあとの次のフレームで、ふわっと。
// 読み込みが速くてもロゴが見えるよう、開いてから BOOT_MIN_MS までは出しておく（遅いときは待たせる時間を足さない）
export const BOOT_MIN_MS = 700;
// 絵を作るだけのモード（page.php の ?render=1。「声で動かす」が見えない iframe で開き、applySave で替えたキャラを画像にする）。
// 画面の一覧（はじめのカテゴリのパーツ・先読み）は作らない（2026-10-23、「声で動かす」の PageSpeed）
const RENDER = !!window.CREATE_RENDER;
export function hideBoot() {
  const boot = $('boot');
  if (!boot) { $('app').classList.remove('booting'); appIsShown(); return; }
  // 絵を作るだけのモード（?render=1）は人に見せないので、ロゴを見せる間も消える動きも待たない（「声で動かす」は #boot が消えるのを待っている。2026-10-23）
  if (RENDER) { $('app').classList.remove('booting'); boot.remove(); appIsShown(); return; }
  if (!agreed()) { S.bootWaitsAgree = true; return; }
  const wait = Math.max(0, BOOT_MIN_MS - performance.now());
  setTimeout(() => { requestAnimationFrame(() => {
    $('app').classList.remove('booting'); // 組み立てている間は見えないことにしていた（index.php の .app.booting）
    boot.classList.add('out');
    boot.setAttribute('aria-hidden', 'true');
    setTimeout(() => { boot.remove(); appIsShown(); }, 500);
  }); }, wait);
}
export function start() {
  // 中身（core/）からの知らせを画面の部品へ: 位置などを付け直したら枠の描き直しとオートセーブ、保存データを読んだら名前
  on('placed', () => { scheduleBox(); scheduleAutosave(); });
  on('applied', drawName);
  setStatus('読み込み中…');
  const t0 = performance.now();
  // 起動の処理は、いくつかのタスクに分ける（読み込み中の画面のあいだも、1つの長いタスクだとその間のタップが待たされる。2026-10-18）
  let restored: SaveData | null = null, auto: SaveData | null = null, from = 'はじめのキャラ', firstKey: string | null | undefined = null;
  // アプリ内ブラウザから開き直してきたキャラ（#chara=）は作りかけに入れる（URL からはすぐ消す。前は additional.js がしていた）
  const carried = takeCarried();
  loadData().then(later).then(() => {
    buildStage();
    buildCategories();
    // 読み込む順は本物の chibi.loadDefault と同じ: 画像からの復元（sessionStorage の avatarData、読めたら消す）→ 作りかけ → はじめのキャラ
    let av = null;
    try { av = sessionStorage.getItem('avatarData'); } catch (e) {}
    return av ? unzip(av).then((t) => { const save = JSON.parse(t); try { sessionStorage.removeItem('avatarData'); } catch (e) {} return save; }).catch(() => null) : null;
  }).then((save) => {
    restored = save;
    return restored ? null : carried.then(() => readStored(AUTO_KEY));
  }).then((stored) => {
    auto = stored;
    const firstTab = Array.prototype.filter.call(document.querySelectorAll('#cats .cat'), (b) => b.dataset.key !== 'chara')[0];
    firstKey = firstTab ? firstTab.dataset.key : keyOf(C.cats[1] || C.cats[0]);
    // キャラに使うカテゴリと、はじめに開くカテゴリのパーツを、1つずつ別のタスクで読んでおく（applySave で読むと全部が1つのタスクになる）
    return warmParts(restored || auto || defaultSave(), RENDER ? null : firstKey);
  }).then(() => {
    applySave(restored || auto || defaultSave());
    if (restored) from = '受け取ったキャラ'; else if (auto) from = '作りかけのキャラ';
    return later();
  }).then(() => {
    if (!RENDER) {
      selectCategory(firstKey!);
      markInput(); // 画面が出た直後はすぐ触られることが多いので、先読みは少し待ってから
      prefetchParts();
      // 名前の書体は、起動が落ち着いてから裏で読んでおく（完成のときに待たないように。名前を入れればその場で読む）
      setTimeout(() => { whenIdle(loadNameFont); }, 3000);
    }
    S.autosaveReady = true;
    histReset(); // ここから先が元に戻せる
    if (restored) scheduleAutosave(); // 受け取ったキャラは作りかけとして残す（本物も復元のあとカテゴリを替えると残る）
    // はじめての人への同意の画面は index.php がもう出している。出ていなければ（index.php の古い版など）ここで
    if (!agreed() && $('first').hidden) setTimeout(openFirst, Math.max(0, BOOT_MIN_MS - performance.now()) + 300);
    setStatus(from + 'を表示中（本物の CHOCO と同じ保存場所）・' + C.cats.length + ' カテゴリ・読み込み ' + Math.round(performance.now() - t0) + 'ms');
    hideBoot();
  }).catch((e) => {
    setStatus('読み込めませんでした: ' + e.message);
    // 読み込み中の画面のまま、知らせる（組み立て途中の画面は見せない）
    if ($('bootMsg')) $('bootMsg').textContent = txt('load_failed');
    console.error(e);
  });

  bindPad();
  bindTour();
  bindFinish();
  // ページを離れるとき・裏に回ったときは、すぐに書き終える（ブラウザの圧縮を待たない）
  window.addEventListener('pagehide', () => { flushAutosave(true); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flushAutosave(true); });
  $('app').classList.toggle('no-anim', !anim);
  // 画面の大きさが変わったら（向きを変えた・PC とスマホの並びが入れかわった）、選んだ印を置き直す
  // PC の並び（名前がいつも出ている）とスマホの並びが入れかわる途中は、名前の幅が % で決まっていて計算が合わないので、
  // 選んだカテゴリの名前が開き終わったところでも置き直す（ふだんの選び直しでも、見積もりのずれをここで直す）
  let resizeTimer: number | null = null, resized = false;
  window.addEventListener('resize', () => {
    resized = true;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => { placeIndicator(false, true); }, 120);
  });
  // 帯を自分で動かしたあとは、スクロールの合わせ直しをしない（押したときは selectCategory で戻す）
  const strip = $<CatStrip>('cats');
  ['wheel', 'touchstart', 'pointerdown'].forEach((type) => { strip.addEventListener(type, () => { strip._userScrolled = true; }, { passive: true }); });
  strip.addEventListener('transitionend', (e) => {
    if (e.propertyName !== 'max-width' || !((e.target as Element).parentNode as Element).classList.contains('sel')) return;
    placeIndicator(true, resized || !strip._userScrolled);
    resized = false;
  });
  bindStage();
  bindBox();
  bindHistory();
  fitPageFrame();
  bindGrip();
  $('btnRandom').addEventListener('click', randomize);
  $('btnView').addEventListener('click', resetView);
  $('nameChip').addEventListener('click', openNameEditor);
  // 名前をメニューから（全身のメーカーのスマホは名前の欄をキャラの上に出さない。名前の無いメーカーにはボタンが無い）
  const menuName = document.getElementById('btnMenuName');
  if (menuName) menuName.addEventListener('click', () => { toggleMenu(false); openNameEditor(); });
  bindSide();
  bindPartsNav();
  $('btnRandomOpt').addEventListener('click', openRandomSettings);
  $('btnLock').addEventListener('click', () => {
    if (!S.selKey) return;
    setLock(S.selKey, !isLocked(S.selKey));
    toast(txt(isLocked(S.selKey) ? 'lock_on' : 'lock_off', { name: catLabel(S.selKey) }));
  });
  $('btnFinish').addEventListener('click', finish);
  $('btnSlots').addEventListener('click', () => { openSlots(); });
  document.querySelectorAll<HTMLElement>('.tool').forEach((b) => { b.addEventListener('click', () => { useTool(b.dataset.tool); }); });
  bindOpacity();
  $('toastUndo').addEventListener('click', undo);

  $('btnMenu').addEventListener('click', (e) => { e.stopPropagation(); toggleMenu(); });
  // 言語: 共通のヘッダーと同じく ?hl= の URL へ（router.php がその言語の URL に移す）
  $<HTMLSelectElement>('langSelect').addEventListener('change', function () { flushAutosave(true); location.href = this.value; });
  document.addEventListener('click', (e) => { if (!$('menu').hidden && !(e.target as Element).closest('.float.tl')) toggleMenu(false); });
  $('modalClose').addEventListener('click', closeModal);
  $('modal').addEventListener('click', (e) => { if (e.target === $('modal')) closeModal(); });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (!$('modal').hidden) closeModal();
    else if (!$('menu').hidden) toggleMenu(false);
  });
}
